import { useCallback, useEffect, useRef, useState } from 'react'

// Thin React wrapper over the Web Speech API. iOS Safari ships the
// webkit-prefixed constructor (`webkitSpeechRecognition`), modern
// Chromium ships the unprefixed one — we feature-detect via a
// module-level helper so we never store the constructor in React
// state (a previous version did `setRecognition(SR)` and React's
// setState detected a function argument, invoked it to "compute"
// the new state, and crashed with "Constructor requires 'new'").
//
// Behavior:
//   - lang defaults to 'en-US'; pass 'en-GB' or another tag for
//     accent-specific recognition.
//   - We use single-shot recognition (continuous=false). For our
//     drill UX the user taps "Start" → speaks one phrase → we
//     transcribe and stop automatically.
//   - The hook returns `transcript` (final result), `interim`
//     (live partial results so the UI can show what's being heard),
//     and a `supported` flag so the caller can fall back gracefully.
function getSpeechRecognition() {
  if (typeof window === 'undefined') return null
  return window.SpeechRecognition || window.webkitSpeechRecognition || null
}

// All hook activity goes through this single logger so the debug
// overlay surfaces every state transition in one prefixed stream
// — easy to grep for "[speak]" when triaging mic issues.
function log(level, ...parts) {
  const msg = '[speak] ' + parts.join(' ')
  // eslint-disable-next-line no-console
  if (level === 'error') console.error(msg)
  else if (level === 'warn') console.warn(msg)
  // eslint-disable-next-line no-console
  else console.log(msg)
}

// Force-release the iOS mic indicator. SpeechRecognition.abort() and
// nulling handlers aren't enough on iOS WKWebView — the orange status
// bar dot can stay lit for tens of seconds after SR session ends.
//
// The trick that works: open a fresh getUserMedia audio stream and
// immediately stop its tracks. iOS treats a track.stop() on a *real*
// audio MediaStreamTrack as a definitive "this page is done with the
// mic" signal in a way that SR's internal cleanup apparently doesn't.
// The brief re-request doesn't prompt for permission again (SR
// already granted it) and the stream lives for one tick, max.
async function forceReleaseMic() {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    stream.getTracks().forEach((track) => {
      try { track.stop() } catch { /* noop */ }
    })
    log('info', 'forceReleaseMic: requested + stopped a fresh audio track')
  } catch (e) {
    // Permission revoked / no device / iOS denied — nothing we can
    // do at this point, and the original SR-driven indicator will
    // clear eventually on its own.
    log('warn', 'forceReleaseMic failed:', e?.message || String(e))
  }
}

export function useSpeechRecognition({ lang = 'en-US' } = {}) {
  // `supported` is computed once via the useState initializer; the
  // constructor reference itself never enters state, sidestepping
  // React's "if you pass a function, I'll call it" updater protocol.
  const [supported] = useState(() => !!getSpeechRecognition())
  const [listening, setListening] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [interim, setInterim] = useState('')
  const [error, setError] = useState(null)
  const recRef = useRef(null)

  // Watchdog timer ref. iOS standalone PWA has webkitSpeechRecognition
  // defined but sometimes start() neither fires onstart nor onerror —
  // it just silently does nothing. The watchdog flips listening back
  // off + surfaces an error so the UI doesn't sit in a stuck state.
  const watchdogRef = useRef(null)
  // Auto-stop timers. iOS WKWebView doesn't reliably honour
  // continuous=false: even after a final transcript arrives the
  // engine often keeps the mic open until the user manually stops.
  // We force-stop on two signals:
  //   - finalStopRef: 350ms after the first final result (gives the
  //     engine a beat to flush any trailing interim text)
  //   - maxDurationRef: 12s hard cap from rec.start() so a quiet user
  //     session can't hold the mic forever.
  const finalStopRef = useRef(null)
  const maxDurationRef = useRef(null)

  const start = useCallback(() => {
    log('info', 'start() called, listening=', listening)
    const SR = getSpeechRecognition()
    if (!SR) {
      log('warn', 'start: no SpeechRecognition constructor available')
      return
    }
    if (listening) {
      log('warn', 'start: already listening, ignoring')
      return
    }
    // Standalone PWA detection — useful triage signal because iOS
    // WKWebView in standalone mode is the most common silent-fail env.
    const isStandalone = (typeof navigator !== 'undefined' && navigator.standalone === true)
      || (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
    log('info', 'env: standalone=', String(isStandalone), 'ua=', (typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a').slice(0, 120))

    setError(null)
    setTranscript('')
    setInterim('')
    let rec
    try {
      rec = new SR()
      log('info', 'new SR() ok')
    } catch (e) {
      log('error', 'new SR() threw:', e?.message || String(e))
      setError(e?.message || 'recognition init failed')
      return
    }
    rec.lang = lang
    rec.interimResults = true
    rec.continuous = false
    rec.maxAlternatives = 1

    rec.onstart = () => {
      log('info', 'event onstart')
      if (watchdogRef.current) {
        clearTimeout(watchdogRef.current)
        watchdogRef.current = null
      }
      setListening(true)
    }
    rec.onaudiostart = () => {
      log('info', 'event onaudiostart')
      if (watchdogRef.current) {
        clearTimeout(watchdogRef.current)
        watchdogRef.current = null
      }
      setListening(true)
    }
    rec.onspeechstart = () => log('info', 'event onspeechstart')
    rec.onspeechend = () => log('info', 'event onspeechend')
    rec.onaudioend = () => log('info', 'event onaudioend')
    rec.onnomatch = () => log('warn', 'event onnomatch')
    rec.onresult = (event) => {
      let finalText = ''
      let interimText = ''
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i]
        if (r.isFinal) finalText += r[0]?.transcript || ''
        else interimText += r[0]?.transcript || ''
      }
      log('info', 'event onresult final="' + finalText + '" interim="' + interimText + '"')
      if (finalText) setTranscript((prev) => (prev + ' ' + finalText).trim())
      setInterim(interimText)
      // Auto-stop after a final result. iOS WKWebView doesn't reliably
      // fire onspeechend → onend after continuous=false sessions, so
      // we nudge it. 350ms gives the engine time to flush any
      // remaining interim updates before we call stop().
      if (finalText && !finalStopRef.current) {
        finalStopRef.current = setTimeout(() => {
          finalStopRef.current = null
          log('info', 'auto-stop on final result')
          try { rec.stop?.() } catch { /* noop */ }
        }, 350)
      }
    }
    rec.onerror = (event) => {
      // Common errors: 'no-speech', 'not-allowed', 'audio-capture',
      // 'service-not-allowed' (iOS PWA-specific — the standalone
      // WKWebView blocks SR even though the constructor is defined).
      log('error', 'event onerror code="' + (event?.error || 'unknown') + '" message="' + (event?.message || '') + '"')
      if (watchdogRef.current) {
        clearTimeout(watchdogRef.current)
        watchdogRef.current = null
      }
      if (finalStopRef.current) {
        clearTimeout(finalStopRef.current)
        finalStopRef.current = null
      }
      if (maxDurationRef.current) {
        clearTimeout(maxDurationRef.current)
        maxDurationRef.current = null
      }
      setError(event?.error || 'recognition error')
      setListening(false)
      // Same iOS-mic-teardown sequence as onend — error paths can
      // leave the mic indicator lit too if we don't fully drop the
      // recogniser. abort() is a no-op on an already-stopped session.
      try { rec.abort?.() } catch { /* noop */ }
      if (recRef.current === rec) recRef.current = null
      forceReleaseMic()
    }
    rec.onend = () => {
      log('info', 'event onend')
      if (watchdogRef.current) {
        clearTimeout(watchdogRef.current)
        watchdogRef.current = null
      }
      if (finalStopRef.current) {
        clearTimeout(finalStopRef.current)
        finalStopRef.current = null
      }
      if (maxDurationRef.current) {
        clearTimeout(maxDurationRef.current)
        maxDurationRef.current = null
      }
      setListening(false)
      setInterim('')
      // Aggressive teardown to release the iOS mic indicator. iOS
      // Safari/WKWebView keeps the orange "mic in use" status bar
      // dot lit until the SpeechRecognition instance is fully
      // released — recognition `onend` alone isn't enough. Calling
      // abort() + nulling every event handler + dropping our ref
      // lets the engine drop the underlying MediaStream and clears
      // the indicator within a frame or two.
      try { rec.abort?.() } catch { /* noop */ }
      try {
        rec.onresult = null
        rec.onerror = null
        rec.onstart = null
        rec.onaudiostart = null
        rec.onaudioend = null
        rec.onspeechstart = null
        rec.onspeechend = null
        rec.onnomatch = null
        rec.onend = null
      } catch { /* some browsers freeze handlers; ignore */ }
      if (recRef.current === rec) recRef.current = null
      // Fire-and-forget — open a real audio MediaStreamTrack and
      // immediately stop it. iOS WKWebView needs this signal to
      // clear the orange status-bar mic indicator. See the function
      // comment for why SR's own abort() isn't enough.
      forceReleaseMic()
    }

    try {
      log('info', 'calling rec.start() lang=' + lang)
      rec.start()
      recRef.current = rec
      // 12s hard cap on a single recording. Even if iOS never fires
      // onspeechend or a final result, this guarantees the mic
      // releases. Cleared on natural onend / onerror.
      if (maxDurationRef.current) clearTimeout(maxDurationRef.current)
      maxDurationRef.current = setTimeout(() => {
        maxDurationRef.current = null
        log('warn', 'max-duration cap (12s) reached — forcing stop')
        try { rec.stop?.() } catch { /* noop */ }
      }, 12000)
      log('info', 'rec.start() returned without throwing — arming 3.5s watchdog')
      // Watchdog: if 3.5s elapse without onstart / onaudiostart /
      // onerror, the recogniser is stuck — common on iOS standalone
      // PWA where the API exists but is effectively neutered. We
      // abort and surface a tailored error so the UI prompts the
      // user to switch to mobile Safari.
      watchdogRef.current = setTimeout(() => {
        watchdogRef.current = null
        if (!recRef.current) return
        log('error', 'watchdog tripped — no onstart/onaudiostart/onerror in 3.5s; aborting recogniser')
        try { recRef.current.abort?.() } catch (e) { log('warn', 'abort threw:', e?.message || String(e)) }
        setListening(false)
        setError('no-start')
      }, 3500)
    } catch (e) {
      log('error', 'rec.start() threw:', e?.message || String(e))
      setError(e?.message || 'start failed')
      setListening(false)
    }
  }, [listening, lang])

  const stop = useCallback(() => {
    log('info', 'stop() called')
    if (!recRef.current) {
      log('warn', 'stop: no active recogniser')
      return
    }
    try { recRef.current.stop() } catch (e) { log('warn', 'stop threw:', e?.message || String(e)) }
  }, [])

  const reset = useCallback(() => {
    setTranscript('')
    setInterim('')
    setError(null)
  }, [])

  // Clean up on unmount — abort any in-flight session so it doesn't
  // keep the microphone open after a navigation away, and clear all
  // pending timers so they don't fire onto a stale setState.
  useEffect(() => {
    return () => {
      if (watchdogRef.current) {
        clearTimeout(watchdogRef.current)
        watchdogRef.current = null
      }
      if (finalStopRef.current) {
        clearTimeout(finalStopRef.current)
        finalStopRef.current = null
      }
      if (maxDurationRef.current) {
        clearTimeout(maxDurationRef.current)
        maxDurationRef.current = null
      }
      try { recRef.current?.abort?.() } catch { /* noop */ }
    }
  }, [])

  return {
    supported,
    listening,
    transcript,
    interim,
    error,
    start,
    stop,
    reset,
  }
}

// Char-level normalised Levenshtein similarity, 0-100. Used to score
// a pronunciation attempt against the target phrase.
//
// Picks character-level over word-level because vocab drills are
// usually a single word — word-level would be either 100 or 0, with
// no partial credit for "tree" vs "three" or "ship" vs "sheep". The
// underlying Levenshtein still picks up those near-misses with a
// realistic gradient.
export function pronunciationSimilarity(target, transcript) {
  if (!target || !transcript) return 0
  const a = normalise(target)
  const b = normalise(transcript)
  if (!a || !b) return 0
  if (a === b) return 100
  const dist = levenshtein(a, b)
  const maxLen = Math.max(a.length, b.length)
  return Math.max(0, Math.round((1 - dist / maxLen) * 100))
}

function normalise(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim()
}

function levenshtein(a, b) {
  const m = a.length
  const n = b.length
  if (m === 0) return n
  if (n === 0) return m
  // Single-row rolling buffer keeps memory at O(n).
  const prev = new Array(n + 1)
  const cur = new Array(n + 1)
  for (let j = 0; j <= n; j++) prev[j] = j
  for (let i = 1; i <= m; i++) {
    cur[0] = i
    for (let j = 1; j <= n; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1
      cur[j] = Math.min(
        cur[j - 1] + 1,        // insertion
        prev[j] + 1,           // deletion
        prev[j - 1] + cost,    // substitution
      )
    }
    for (let j = 0; j <= n; j++) prev[j] = cur[j]
  }
  return prev[n]
}
